import * as React from "react";
import { useMemo, useContext } from "react";
import * as Immutable from "immutable";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Table } from "components/bootstrap";
import { Spinner } from "components/common";
import HelpPanel from "components/event-definitions/common/HelpPanel";
import generateId from "logic/generateId";
import useSearchExecutors from "views/components/contexts/useSearchExecutors";
import Search from "views/logic/search/Search";
import createSearch from "views/logic/slices/createSearch";
import SearchExecutionState from "views/logic/search/SearchExecutionState";
import Query from "views/logic/queries/Query";
import Parameter from "views/logic/parameters/Parameter";
import usePermissions from "hooks/usePermissions";
import FilterPreviewResults from "components/event-definitions/event-definition-types/FilterPreviewResults";
import useDebouncedValue from "hooks/useDebouncedValue";
import StreamsContext from "contexts/StreamsContext";
const Messages = ({ messages }) => messages.map(({ index, message }) => /* @__PURE__ */ React.createElement("tr", { key: `${index}-${message._id}` }, /* @__PURE__ */ React.createElement("td", null, message.timestamp), /* @__PURE__ */ React.createElement("td", null, message.message)));
const SearchResult = ({
  searchResult,
  isFetchingData
}) => {
  const { t } = useTranslation("event_definitions");
  if (isFetchingData) return /* @__PURE__ */ React.createElement(Spinner, { text: t("filter_preview.loading") });
  if (!searchResult.messages || searchResult.messages.length === 0) {
    return /* @__PURE__ */ React.createElement("p", null, t("filter_preview.no_messages"));
  }
  return /* @__PURE__ */ React.createElement(Table, { condensed: true, bordered: true }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("th", null, t("filter_preview.timestamp")), /* @__PURE__ */ React.createElement("th", null, t("filter_preview.message")))), /* @__PURE__ */ React.createElement("tbody", null, /* @__PURE__ */ React.createElement(Messages, { messages: searchResult.messages })));
};
const constructSearch = (config, searchTypeId, queryId, streams) => {
  var _a, _b, _c;
  const combinedStreamsAndCategories = /* @__PURE__ */ new Set([
    ...(config == null ? void 0 : config.streams) || [],
    ...((_a = config == null ? void 0 : config.stream_categories) == null ? void 0 : _a.flatMap(
      (category) => streams.filter((s) => s.categories.includes(category)).map((s) => s.id)
    )) || []
  ]);
  const formattedStreams = [...combinedStreamsAndCategories].map((stream) => ({ type: "stream", id: stream })) || [];
  const queryBuilder = Query.builder().id(queryId).query({ type: "elasticsearch", query_string: (config == null ? void 0 : config.query) || "*" }).timerange({ type: "relative", range: ((config == null ? void 0 : config.search_within_ms) || 0) / 1e3 }).filter(formattedStreams.length === 0 ? null : Immutable.Map({ type: "or", filters: formattedStreams })).filters(config.filters).searchTypes([
    {
      id: searchTypeId,
      type: "messages",
      limit: 10,
      offset: 0,
      filter: void 0,
      filters: void 0,
      name: void 0,
      query: void 0,
      timerange: void 0,
      streams: [],
      stream_categories: [],
      sort: [],
      decorators: []
    }
  ]);
  const query = queryBuilder.build();
  return Search.create().toBuilder().parameters(
    (_c = (_b = config == null ? void 0 : config.query_parameters) == null ? void 0 : _b.filter((param) => !param.embryonic).map((param) => Parameter.fromJSON(param))) != null ? _c : []
  ).queries([query]).build();
};
const isPermittedToSeePreview = (isPermitted, config) => {
  var _a;
  const missingPermissions = (_a = config == null ? void 0 : config.streams) == null ? void 0 : _a.some((stream) => !isPermitted(`streams:read:${stream}`));
  return !missingPermissions;
};
const useExecutePreview = (config) => {
  var _a, _b, _c, _d, _e;
  const streams = useContext(StreamsContext);
  const { isPermitted } = usePermissions();
  const queryId = useMemo(() => generateId(), []);
  const searchTypeId = useMemo(() => generateId(), []);
  const { startJob, executeJobResult } = useSearchExecutors();
  const permitted = isPermittedToSeePreview(isPermitted, config);
  const [debouncedConfig] = useDebouncedValue(config, 250);
  const hasEmbryonicParams = (_a = debouncedConfig == null ? void 0 : debouncedConfig.query_parameters) == null ? void 0 : _a.some(
    (param) => param.embryonic
  );
  const { data, isFetching } = useQuery({
    queryKey: ["filter-preview", debouncedConfig, searchTypeId, queryId, streams],
    queryFn: async () => {
      const search = constructSearch(debouncedConfig, searchTypeId, queryId, streams);
      const createdSearch = await createSearch(search);
      const jobIds = await startJob(createdSearch, [searchTypeId], SearchExecutionState.empty());
      const result = await executeJobResult({ jobIds });
      return result;
    },
    enabled: permitted && !hasEmbryonicParams,
    retry: false
  });
  return {
    isFetching,
    errors: (_b = data == null ? void 0 : data.result) == null ? void 0 : _b.errors,
    result: (_e = (_d = (_c = data == null ? void 0 : data.result) == null ? void 0 : _c.forId(queryId)) == null ? void 0 : _d.searchTypes) == null ? void 0 : _e[searchTypeId]
  };
};
const FilterPreview = ({ config }) => {
  const { t } = useTranslation("event_definitions");
  const { isPermitted } = usePermissions();
  const displayPreview = isPermittedToSeePreview(isPermitted, config);
  const { isFetching, result: searchResult = {}, errors = [] } = useExecutePreview(config);
  const isFetchingData = isFetching || !searchResult;
  const hasError = (errors == null ? void 0 : errors.length) > 0;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(HelpPanel, { collapsible: true, defaultExpanded: !displayPreview, title: t("filter_preview.help_title") }, /* @__PURE__ */ React.createElement("p", null, t("filter_preview.help_description")), /* @__PURE__ */ React.createElement("ul", null, /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("b", null, t("filter_preview.help_filter_label")), "\u2003", t("filter_preview.help_filter")), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("b", null, t("filter_preview.help_aggregation_label")), "\u2003", t("filter_preview.help_aggregation")), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("b", null, t("filter_preview.help_aggregation_groups_label")), "\u2003", t("filter_preview.help_aggregation_groups")))), displayPreview && /* @__PURE__ */ React.createElement(FilterPreviewResults, { hasError }, hasError ? /* @__PURE__ */ React.createElement("p", null, errors[0].description) : /* @__PURE__ */ React.createElement(SearchResult, { isFetchingData, searchResult })));
};
export default FilterPreview;
